import { loadPreferences, myPreferences, savePreferences } from '@/appLogic/view/preferences';

/**
 * テーブル設定の永続化・初期化を行うカスタムフック
 * ページサイズやソート状態を IndexedDB (myPreferences) に保存・読み込み可能
 */
export const useTablePreferences = () => {
  /**
   * 指定したテーブルキーの設定を取得、存在しない場合は初期化
   * @param tableKey - テーブル識別キー
   * @returns テーブル設定オブジェクト
   */
  const _ensure = (tableKey: string) => {
    if (!myPreferences[tableKey]) {
      myPreferences[tableKey] = {};
    }
    return myPreferences[tableKey];
  };

  /**
   * テーブル設定を state に初期化
   * @param tableKey - テーブル識別キー
   * @param state - 初期化する TableState
   * @param initPagingOptions - ページサイズ初期化関数
   */
  const initTablePreferences = async (tableKey: string, state: TableState, initPagingOptions: (size: number) => { pageSize: number }) => {
    await loadPreferences();
    const pref = myPreferences[tableKey];
    if (!pref) return;

    if (pref.sortKey) state.tableContents.sortKey = pref.sortKey;
    state.tableContents.isAsc = pref.isAsc ?? true;

    if (pref.pageSize) {
      if (state.tableContents.pagingOptions) {
        state.tableContents.pagingOptions.pageSize = pref.pageSize;
      } else {
        state.tableContents.pagingOptions = {
          ...initPagingOptions(pref.pageSize),
          pageSize: pref.pageSize,
        };
      }
    }

    _applySortUI(state);
  };

  /**
   * ページサイズを保存
   * @param tableKey - テーブル識別キー
   * @param pageSize - 保存するページサイズ
   */
  const savePageSize = async (tableKey: string, pageSize: number) => {
    await loadPreferences();
    const pref = _ensure(tableKey);
    pref.pageSize = pageSize;
    await savePreferences();
  };

  /**
   * ソート設定を保存
   * @param tableKey - テーブル識別キー
   * @param sortKey - ソートキー
   * @param isAsc - 昇順かどうか
   */
  const saveSort = async (tableKey: string, sortKey: string, isAsc: boolean) => {
    await loadPreferences();
    const pref = _ensure(tableKey);
    pref.sortKey = sortKey;
    pref.isAsc = isAsc;
    await savePreferences();
  };

  /**
   * UI上のソート表示を更新
   * @param state - 更新対象の TableState
   */
  const _applySortUI = (state: TableState) => {
    if (!state.tableContents.sortKey) return;

    const currentIsAsc = state.tableContents.isAsc ?? true;

    state.tableContents.tableHeaders.contentProperties = state.tableContents.tableHeaders.contentProperties.map((property) => ({
      ...property,
      sortOption: {
        ...property.sortOption,
        isAsc: property.key === state.tableContents.sortKey ? currentIsAsc : true,
      },
    }));
  };

  return {
    initTablePreferences,
    savePageSize,
    saveSort,
  };
};

/**
 * テーブル表示状態の型
 */
export type TableState = {
  tableContents: {
    sortKey?: string;
    isAsc?: boolean;
    pagingOptions?: {
      pageSize: number;
    };
    tableHeaders: {
      contentProperties: Array<{
        key: string;
        sortOption: {
          isAsc: boolean;
        };
      }>;
    };
  };
};
